import React, { useState } from 'react';
import { Rnd } from 'react-rnd';
import { IconButton, Box, useTheme } from '@mui/material';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

interface AdjustablePropData {
  propName: string;
  propImage: string;
  width: number;
  height: number;
  xAxis: number;
  yAxis: number;
  rotation: number;
  propIcon: string;
  isPlace: boolean;
  placeId: string;
}

interface AdjustablePropProps {
  prop: AdjustablePropData;
  index: number;
  onUpdate: (index: number, updated: AdjustablePropData) => void;
  onDelete: (index: number) => void;
  onDuplicate: (index: number) => void;
}

const AdjustableProp: React.FC<AdjustablePropProps> = ({
  prop,
  index,
  onUpdate,
  onDelete,
  onDuplicate,
}) => {
  const theme = useTheme();
  const [hovered, setHovered] = useState(false);

  const handleRotate = (angle: number) => {
    const newRotation = (prop.rotation + angle + 360) % 360;
    onUpdate(index, { ...prop, rotation: newRotation });
  };

  return (
    <Rnd
      key={`prop-${index}`}
      default={{
        x: prop.xAxis,
        y: prop.yAxis,
        width: prop.width,
        height: prop.height,
      }}
      bounds="parent"
      onDragStop={(e, d) =>
        onUpdate(index, { ...prop, xAxis: d.x, yAxis: d.y })
      }
      onResizeStop={(e, dir, ref, delta, position) => {
        onUpdate(index, {
          ...prop,
          width: parseInt(ref.style.width),
          height: parseInt(ref.style.height),
          xAxis: position.x,
          yAxis: position.y,
        });
      }}
      style={{ zIndex: 30 }}
    >
      <Box
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        sx={{
          width: '100%',
          height: '100%',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        {/* Top Controls */}
        {hovered && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              px: 0.5,
              pt: 0.5,
              zIndex: 5,
            }}
          >
            <IconButton
              size="small"
              onClick={() => onDuplicate(index)}
              sx={{ bgcolor: theme.palette.background.default, boxShadow: 1 }}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => onDelete(index)}
              sx={{ bgcolor: theme.palette.background.default, boxShadow: 1 }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        {/* Image */}
        <img
          src={prop.propImage}
          alt={prop.propName}
          width="100%"
          height="100%"
          style={{
            transform: `rotate(${prop.rotation}deg)`,
            pointerEvents: 'none',
            transition: 'transform 0.2s ease',
          }}
        />

        {/* Bottom Controls */}
        {hovered && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              px: 0.5,
              pb: 0.5,
              zIndex: 5,
            }}
          >
            <IconButton
              size="small"
              onClick={() => handleRotate(-15)}
              sx={{ bgcolor: theme.palette.background.default, boxShadow: 1 }}
            >
              <RotateLeftIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handleRotate(15)}
              sx={{ bgcolor: theme.palette.background.default, boxShadow: 1 }}
            >
              <RotateRightIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>
    </Rnd>
  );
};

export { AdjustableProp };
